import { GraduationCap, Library, History, PenBox, FilePen, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useEffect, useRef, useState } from 'react'
import PageChat from '@/components/page-chat.tsx'
import PageLibrary from '@/components/page-library.tsx'
import PagePractice from '@/components/page-practice.tsx'
import useGlobalStore from '@/useGlobalStore.ts'
import { useTranslation } from 'react-i18next'
import { Chat, ChatEntry, User } from '@/models.ts'
import { Drawer, DrawerTrigger } from '@/components/ui/drawer'
import PageSettings from '@/components/page-settings.tsx'
import PageSideDrawer from '@/components/page-side-drawer.tsx'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import dayjs from 'dayjs'
import { Device } from '@capacitor/device'
import NavLanguageSelector from '@/components/nav-language-selector.tsx'
import LingoFlowIcon from '@/components/LingoFlowIcon.tsx'
import { cn } from '@/lib/utils.ts'
import usePreferences from '@/usePreferences.ts'
import WordDetailsDialog from '@/components/word-details-dialog.tsx'
import { createPortal } from 'react-dom'
import { dispatch, useBus } from '@/useBus.ts'
import api from '@/api.ts'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { AnimatePresence, motion } from 'motion/react'
import PageSearch from '@/components/page-search.tsx'
import OfflineBanner from '@/components/offline-banner.tsx'
import AuthDialog from '@/components/auth-dialog.tsx'
import { Capacitor } from '@capacitor/core'
import { toast } from 'sonner'
import PurchaseSubscriptionDialog from '@/components/purchase-subscription-dialog.tsx'
import { LOG_LEVEL, Purchases, PurchasesOffering } from '@revenuecat/purchases-capacitor'
import SubscriptionManageDialog from '@/components/subscription-manage-dialog.tsx'
import { App as CapacitorApp } from '@capacitor/app'
import PageCollections from '@/components/page-collections.tsx'
import CollectionDetailsDialog from '@/components/collection-details-dialog.tsx'

dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(utc)

const defaultPage = 1

const App = () => {
	const { t } = useTranslation()
	const [previousCurrentPage, setPreviousCurrentPage] = useState<number>(defaultPage)
	const [currentPage, setCurrentPage] = useState<number>(defaultPage)
	const {
		isAuthLoaded,
		user,
		setUser,
		learningLanguageCode,
		chatHistoryList,
		changeLearningLanguage,
		loadLibraryOverview,
		resetChat,
		loadChatAsActiveChat,
		appLanguageCode,
		loadPracticeOverview,
		currentPracticeStatus,
		setAppNewsHtml,
		loadChatHistoryList,
		loadLibraryCollectionsList,
		setIsAuthLoaded,
		setIsTrialExpired,
		setIsPracticeOverviewLoading,
		setIsLibraryOverviewLoading,
		isModalVisible,
		isSheetVisible,
		availableAppLanguages,
		isLoadingChatHistory,
		setIsLoadingChatHistory,
		setActiveChat,
		setChatEntries,
		addChatEntry,
	} = useGlobalStore()
	const {
		removeItem: removePreferenceItem,
		getItem: getPreferenceItem,
		setAppPreferencesLoaded,
		setItem: setPreferenceItem,
	} = usePreferences()
	const [authMessage, setAuthMessage] = useState<string>('')
	const [showPurchaseDialogOnAuthCompletion, setShowPurchaseDialogOnAuthCompletion] = useState(false)
	const [authPageDismissible, setAuthPageDismissible] = useState(true)
	const [showAuthPage, setShowAuthPage] = useState<boolean>(false)
	const [showPurchaseSubscriptionDialog, setShowPurchaseSubscriptionDialog] = useState<boolean>(false)
	const [purchaseSubscriptionDialogMode, setPurchaseSubscriptionDialogMode] = useState<string>('basic')
	const [showManageSubscriptionDialog, setShowManageSubscriptionDialog] = useState<boolean>(false)
	const showManageSubscriptionDialogRef = useRef(showManageSubscriptionDialog)
	const chatHistoryDrawerTrigger = useRef<any>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const [isPracticeInProgressConfirmationShown, setIsPracticeInProgressConfirmationShown] = useState(false)
	const [lastActionBeforeBlockingDialog, setLastActionBeforeBlockingDialog] = useState<any>({})
	const [showSearchBar, setShowSearchBar] = useState<boolean>(false)
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [appCurrentOffering, setAppCurrentOffering] = useState<PurchasesOffering | null>(null)
	const [isUserAuthenticationLoading, setIsUserAuthenticationLoading] = useState<boolean>(false)
	const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false)

	const stateRef = useRef({
		isModalVisible: false,
		isSheetVisible: false,
		currentPage: defaultPage,
		previousCurrentPage: defaultPage,
		showSearchBar: false,
		showPurchaseSubscriptionDialog: false,
		showManageSubscriptionDialog: false,
	})

	useBus(
		'toggle-purchase-subscription-dialog',
		({ val, mode }) => {
			if (isAuthLoaded && (user === null || user.trial_expires_at !== null)) {
				// need to register first
				dispatch('show-auth-dialog', {
					showPurchaseDialogOnAuthCompletion: true,
				})
			} else {
				setShowPurchaseSubscriptionDialog(val)
				setPurchaseSubscriptionDialogMode(mode)
			}
		},
		[isAuthLoaded, user],
	)

	useBus('toggle-manage-subscription-dialog', (val) => {
		setShowManageSubscriptionDialog(val)
	})

	useBus(
		'show-chat-data',
		async ({ chat, response_entries }: { chat: Chat; response_entries: ChatEntry[] }) => {
			setActiveChat(chat)
			setChatEntries(response_entries)
			setCurrentPage(1)

			const lastMessage = response_entries[response_entries.length - 1]

			if (lastMessage === undefined || lastMessage === null) {
				toast(t('Something went wrong, restart LingoFlow'))
				return
			}

			const chatLearningsData = await api(
				`/v1/library/check-for-chat-learnings/${lastMessage.ai_chat_learning_id}`,
				{
					method: 'GET',
				},
			)
			for (const entry of chatLearningsData.library_entries) {
				addChatEntry(entry)
			}

			await loadChatHistoryList()
		},
		[loadChatHistoryList],
	)

	useBus(
		'end-practice',
		() => {
			setIsPracticeInProgressConfirmationShown(true)
		},
		[setIsPracticeInProgressConfirmationShown],
	)

	useBus('show-auth-dialog', (payload) => {
		const messageBase =
			'Create a free account to continue using LingoFlow. Your trial account data will also be transferred.'
		if (payload?.trialExpired) {
			setAuthMessage(t('Your trial account has expired.') + ' ' + t(messageBase))
		}
		if (payload?.showPurchaseDialogOnAuthCompletion) {
			setAuthMessage(t('Login or create an account first. Your trial account data will also be transferred.'))
			setShowPurchaseDialogOnAuthCompletion(true)
		}
		setAuthPageDismissible(payload?.dismissible)
		setShowAuthPage(true)
	})

	useBus('change-page', (payload) => {
		setCurrentPage(payload.page)
	})

	const successfulLoginHandler = () => {
		authenticateUser(true)
		setShowAuthPage(false)
	}

	const generateTrialUser = async (id: string) => {
		return api('/auth/create-trial-user', {
			method: 'POST',
			body: {
				original_platform: Capacitor.getPlatform(),
				trial_id: id,
			},
		}).catch((error) => {
			throw new Error(error.data?.message || 'Signup failed')
		})
	}

	const logOutUser = async () => {
		await removePreferenceItem('user')
		setUser(null)
		setShowAuthPage(true)
	}

	const onSuccessfulAuth = async () => {
		setIsSettingsDrawerOpen(false)

		if (showPurchaseDialogOnAuthCompletion) {
			setShowPurchaseSubscriptionDialog(true)
		}

		await loadChatHistoryList()
		await loadLibraryOverview()
		await loadPracticeOverview()
		await loadLibraryCollectionsList()

		if (Capacitor.getPlatform() !== 'web') {
			try {
				Purchases.logIn({ appUserID: user!.email as string }).then()
				Purchases.getOfferings().then((offerings) => {
					setAppCurrentOffering(offerings.current)
				})
			} catch (e: any) {
				console.error('Error fetching Revenuecat offerings', e.message)
			}
		}
	}

	const onAuthDialogClosed = (dismissible: boolean) => {
		if (dismissible) {
			setShowAuthPage(false)
		} else {
			toast(t('You need to create an account before using LingoFlow.'))
		}
	}

	const authenticateUser = (force: boolean = false, refreshSubs: boolean = false) => {
		getPreferenceItem('user').then((value) => {
			if (user === null || force) {
				if (value === null) {
					// no user saved, try creating or getting a free trial user
					Device.getId().then(({ identifier }) => {
						generateTrialUser(identifier)
							.then((user: User) => {
								setIsTrialExpired(false)
								setPreferenceItem('user', JSON.stringify(user)).then()
								authenticateUser()
							})
							.catch(() => {
								setIsTrialExpired(true)
								// probably, user/device id already exists
								dispatch('show-auth-dialog', {
									trialExpired: true,
									dismissible: false,
								})
							})
					})
				} else {
					const _user = JSON.parse(value) as User
					// @ts-ignore
					window.tempAppAuthToken = _user.access_token
					setIsUserAuthenticationLoading(true)
					api(`/v1/user${refreshSubs ? '?refresh_subscriptions=true' : ''}`)
						.then((data: User) => {
							const userForSaving = data
							userForSaving.access_token = _user.access_token as string
							userForSaving.refresh_token = _user.refresh_token as string
							setUser(userForSaving)
							if (
								userForSaving.trial_expires_at !== null &&
								dayjs.utc(userForSaving.trial_expires_at) < dayjs()
							) {
								setIsTrialExpired(true)
								dispatch('show-auth-dialog', {
									trialExpired: true,
								})
							} else {
								setIsTrialExpired(false)
							}
						})
						.catch((error) => {
							if (error.status === 401) {
								// invalid token and could not refresh
								dispatch('show-auth-dialog')
								setIsLoadingChatHistory(false)
								setIsPracticeOverviewLoading(false)
								setIsLibraryOverviewLoading(false)
							}
						})
						.finally(() => {
							setIsAuthLoaded(true)
							setIsUserAuthenticationLoading(false)
							// @ts-ignore
							window.tempAppAuthToken = null
						})
				}
			}
		})
	}

	const fetchAppNews = async () => {
		const newsHtml: string = await api(
			`/v1/app/news?app_language_code=${appLanguageCode}&learning_language_code=${learningLanguageCode}`,
		)
		setAppNewsHtml(newsHtml)
	}

	const showAndFetchChat = async (chatId: number, fromSearch = false) => {
		setShowSearchBar(false)
		setCurrentPage(1)
		if (!fromSearch) {
			chatHistoryDrawerTrigger?.current?.click()
		}
		await loadChatAsActiveChat(chatId)
	}

	const showAndFetchChatFromSidebar = async (chatId: number) => {
		setCurrentPage(1)
		await loadChatAsActiveChat(chatId)
	}

	const onResetChat = () => {
		resetChat(false)
		setCurrentPage(1)
		dispatch('reset-chat')
	}

	const onBottomTabPressed = (tabNumber: number) => {
		setLastActionBeforeBlockingDialog({
			name: 'BOTTOM_TAB',
			page: tabNumber,
		})
		if (currentPracticeStatus === 'started' && tabNumber !== 2) {
			setIsPracticeInProgressConfirmationShown(true)
		} else {
			setCurrentPage(tabNumber)
			dispatch('bottom-tab-pressed', tabNumber)
		}
		setShowSearchBar(false)
	}

	const endCurrentPracticeClicked = () => {
		dispatch('end-current-practice')
		setIsPracticeInProgressConfirmationShown(false)
		if (lastActionBeforeBlockingDialog.name === 'BOTTOM_TAB') {
			setCurrentPage(lastActionBeforeBlockingDialog.page)
		}
	}

	const onShowSearchBar = () => {
		setShowSearchBar(true)
		setPreviousCurrentPage(currentPage)
		setCurrentPage(4)
	}

	const onCloseSearchBar = () => {
		if (searchQuery.length <= 0) {
			setShowSearchBar(false)
			setCurrentPage(previousCurrentPage)
		} else {
			setSearchQuery('')
			searchInputRef?.current?.focus()
		}
	}

	const initRevenueCat = () => {
		Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }).then()
		Purchases.configure({
			apiKey:
				Capacitor.getPlatform() === 'android'
					? import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY
					: import.meta.env.VITE_REVENUECAT_IOS_API_KEY,
		}).then()
	}

	const onSuccessfulPurchase = () => {
		setIsSettingsDrawerOpen(false)
		authenticateUser(true)
		setShowPurchaseSubscriptionDialog(false)
	}

	useEffect(() => {
		if (currentPage === 1) {
			dispatch('chat-bar-visible')
		}
	}, [currentPage])

	// Keep the ref updated with the latest value
	useEffect(() => {
		showManageSubscriptionDialogRef.current = showManageSubscriptionDialog
	}, [showManageSubscriptionDialog])

	useEffect(() => {
		stateRef.current = {
			isModalVisible,
			isSheetVisible,
			currentPage,
			previousCurrentPage,
			showSearchBar,
			showPurchaseSubscriptionDialog,
			showManageSubscriptionDialog,
		}
	}, [
		isModalVisible,
		isSheetVisible,
		currentPage,
		previousCurrentPage,
		showSearchBar,
		showPurchaseSubscriptionDialog,
		showManageSubscriptionDialog,
	])

	useEffect(() => {
		if (user?.access_token) {
			onSuccessfulAuth().then()
		}
	}, [user])

	useEffect(() => {
		authenticateUser()
		getPreferenceItem('learningLanguageCode').then((value) => {
			if (value === null || value === '') {
				setPreferenceItem('learningLanguageCode', 'en-US').then()
			}
			changeLearningLanguage(value as string).then()
		})
		fetchAppNews().then()

		if (Capacitor.getPlatform() !== 'web') {
			initRevenueCat()
		}
	}, [])

	useEffect(() => {
		CapacitorApp.addListener('resume', () => {
			if (showManageSubscriptionDialogRef.current) {
				authenticateUser(true, true)
			}
			// if (localStorage.shouldReloadApp === 'true') {
			// 	LiveUpdates.reload().then()
			// } else {
			// 	LiveUpdates.sync().then((result) => {
			// 		localStorage.shouldReloadApp = result.activeApplicationPathChanged
			// 	})
			// }
		}).then()

		CapacitorApp.addListener('backButton', () => {
			const {
				isModalVisible,
				isSheetVisible,
				currentPage,
				previousCurrentPage,
				showSearchBar,
				showPurchaseSubscriptionDialog,
				showManageSubscriptionDialog,
			} = stateRef.current

			if (isModalVisible) {
				dispatch('close-modal')
			} else if (isSheetVisible) {
				dispatch('close-sheet')
			} else if (showSearchBar || showPurchaseSubscriptionDialog || showManageSubscriptionDialog) {
				if (currentPage === 4 && previousCurrentPage !== null) {
					setCurrentPage(previousCurrentPage)
				}
				setShowSearchBar(false)
				setShowPurchaseSubscriptionDialog(false)
				setShowManageSubscriptionDialog(false)
			} else {
				CapacitorApp.minimizeApp()
			}
		}).then()

		// LiveUpdates.sync().then((result) => {
		// 	localStorage.shouldReloadApp = result.activeApplicationPathChanged
		// })

		return () => {
			CapacitorApp.removeAllListeners().then()
		}
	}, [])

	return (
		<>
			{createPortal(<WordDetailsDialog />, document.body)}
			{createPortal(<CollectionDetailsDialog />, document.body)}
			<AuthDialog
				open={showAuthPage}
				onSuccessfulLogin={successfulLoginHandler}
				onDismissed={onAuthDialogClosed}
				authMessage={authMessage}
				dismissible={authPageDismissible}
			/>
			<PurchaseSubscriptionDialog
				open={
					showPurchaseSubscriptionDialog &&
					(user?.subscription_expires_at === null ||
						dayjs.utc(user?.subscription_expires_at) < dayjs().utc() ||
						(user?.unsubscribed_at !== null && dayjs.utc(user?.subscription_expires_at) > dayjs().utc()))
				}
				dismissible={true}
				onDismissed={() => setShowPurchaseSubscriptionDialog(false)}
				appCurrentOffering={appCurrentOffering}
				onSuccessfulPurchase={onSuccessfulPurchase}
				mode={purchaseSubscriptionDialogMode}
			/>
			<SubscriptionManageDialog
				open={showManageSubscriptionDialog}
				onDismissed={() => setShowManageSubscriptionDialog(false)}
				isFetchingUser={isUserAuthenticationLoading}
			/>
			<Sheet open={isPracticeInProgressConfirmationShown} onOpenChange={setIsPracticeInProgressConfirmationShown}>
				<SheetContent
					showClose={false}
					dir={availableAppLanguages[appLanguageCode]?.dir}
					side="bottom"
					className="!pb-2 !pt-3">
					<VisuallyHidden.Root>
						<SheetDescription>{t('Practice in progress blocking dialog')}</SheetDescription>
					</VisuallyHidden.Root>
					<SheetHeader>
						<SheetTitle>{t('Practice in progress')}</SheetTitle>
						<div className="text-sm text-muted-foreground">
							<div>{t('You can end the current practice in progress or go back to it.')}</div>
							<div className="flex gap-2 my-4">
								<Button onClick={() => endCurrentPracticeClicked()} className="h-6" variant="ghost">
									{t('End practice')}
								</Button>
								<Button onClick={() => setIsPracticeInProgressConfirmationShown(false)} className="h-6">
									{t('Cancel')}
								</Button>
							</div>
						</div>
					</SheetHeader>
				</SheetContent>
			</Sheet>
			<div
				className={`h-screen ${Capacitor.getPlatform() === 'web' ? 'pb-2' : 'pb-safe'} flex ${availableAppLanguages[appLanguageCode].dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
				<div className="hidden md:flex md:w-1/4">
					<PageSideDrawer
						isLoading={isLoadingChatHistory}
						onChatSelected={showAndFetchChatFromSidebar}
						chatHistoryList={chatHistoryList}
						isInDialog={false}
					/>
				</div>
				<div className="flex flex-col w-full md:w-3/4">
					<header
						dir={showSearchBar ? availableAppLanguages[appLanguageCode]?.dir : 'ltr'}
						className={`shadow-sm px-4 pb-1 ${Capacitor.getPlatform() === 'web' ? 'pt-2' : 'pt-safe'} relative dark:bg-[#121212bf]`}>
						<OfflineBanner className="-mx-4 mb-1" />
						<AnimatePresence mode="wait">
							{showSearchBar ? (
								<motion.div
									key="search"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: 20 }}
									transition={{ duration: 0.1, ease: 'easeOut' }}
									className="flex items-center gap-2 inset-0 bg-background">
									<div className="flex-1 flex items-center gap-2">
										<input
											ref={searchInputRef}
											autoFocus
											type="text"
											placeholder={t('Search...')}
											className="w-full bg-transparent border-none outline-none focus:ring-0"
											value={searchQuery}
											onChange={(val) => setSearchQuery(val.target.value)}
										/>
									</div>
									<Button
										variant="ghost"
										size="icon"
										onClick={onCloseSearchBar}
										className="flex-shrink-0 -me-4">
										<X className="h-5 w-5" />
									</Button>
								</motion.div>
							) : (
								<motion.div
									key="header"
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.1, ease: 'easeOut' }}
									className="grid grid-cols-3">
									<div>
										<Drawer dismissible={true} direction="left">
											<DrawerTrigger className="md:hidden" ref={chatHistoryDrawerTrigger} asChild>
												<Button variant="ghost" size="icon">
													<History className="h-5 w-5" />
												</Button>
											</DrawerTrigger>
											<PageSideDrawer
												isLoading={isLoadingChatHistory}
												onChatSelected={showAndFetchChat}
												chatHistoryList={chatHistoryList}
												isInDialog={true}
											/>
										</Drawer>
										<Button onClick={onResetChat} variant="ghost" size="icon">
											<PenBox className="h-5 w-5" />
										</Button>
									</div>
									<div className="flex justify-center items-center">
										<NavLanguageSelector />
									</div>
									<div className="flex flex-row-reverse">
										<Drawer
											direction="right"
											open={isSettingsDrawerOpen}
											onOpenChange={setIsSettingsDrawerOpen}>
											<DrawerTrigger asChild>
												<Button
													onClick={() => setAppPreferencesLoaded(true)}
													variant="ghost"
													size="icon">
													<LingoFlowIcon className="h-5 w-5" />
												</Button>
											</DrawerTrigger>
											<PageSettings logOutUser={logOutUser} />
										</Drawer>
										<Button onClick={onShowSearchBar} variant="ghost" size="icon">
											<Search className="h-5 w-5" />
										</Button>
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</header>

					<main className="flex-1 overflow-y-auto overflow-x-hidden w-full">
						<PageChat className={cn('h-full', currentPage === 1 ? 'block' : 'hidden')} />
						<PagePractice className={cn('h-full', currentPage === 2 ? 'flex' : 'hidden')} />
						<PageLibrary className={cn('h-full', currentPage === 3 ? 'block' : 'hidden')} />
						<PageSearch
							className={cn('h-full', currentPage === 4 ? 'block' : 'hidden')}
							searchQuery={searchQuery}
							showChat={(id) => showAndFetchChat(id, true)}
						/>
						<PageCollections className={cn('h-full', currentPage === 5 ? 'block' : 'hidden')} />
					</main>

					<nav>
						<div className="flex justify-around">
							<Button
								onClick={() => onBottomTabPressed(1)}
								className={cn(
									'flex-1 flex-col rounded-none h-auto py-2 bg-transparent active:bg-transparent text-muted-foreground transition-transform duration-75 active:scale-110',
									currentPage == 1 ? 'text-foreground' : '',
								)}>
								<GraduationCap className="h-5 w-5" />
								<h6>{t('Learn')}</h6>
							</Button>
							<Button
								onClick={() => onBottomTabPressed(2)}
								className={cn(
									'flex-1 flex-col rounded-none h-auto py-2 bg-transparent active:bg-transparent text-muted-foreground transition-transform duration-75 active:scale-110',
									currentPage == 2 ? 'text-foreground' : '',
								)}>
								<FilePen className="h-5 w-5" />
								<h6>{t('Practice')}</h6>
							</Button>
							<Button
								onClick={() => onBottomTabPressed(3)}
								className={cn(
									'flex-1 flex-col rounded-none h-auto py-2 bg-transparent active:bg-transparent text-muted-foreground transition-transform duration-75 active:scale-110',
									currentPage == 3 ? 'text-foreground' : '',
								)}>
								<Library className="h-5 w-5" />
								<h6>{t('Library')}</h6>
							</Button>
						</div>
					</nav>
				</div>
			</div>
		</>
	)
}

export default App
